import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUsers, useUser, useUserMembershipOps, useCognitoGroups } from '@/api/hooks';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Tooltip } from '@/components/Tooltip';
import { Skeleton, EmptyState } from '@/components/primitives';
import { useSelection, SelectionBar } from '@/components/selection';
import { FilterPopover } from '@/components/FilterPopover';
import { Field } from '@/components/primitives';
import {
  UserActionsMenu,
  EditUserDialog,
  InviteUserDialog,
  AddToTenantsDialog,
  EditUserRoleDialog,
  RemoveUserDialog,
  userName,
} from '@/features/users/user-actions';
import { useIsSuperAdmin } from '@/lib/auth';
import { toast } from 'react-hot-toast';
import {
  Search,
  Users,
  Building2,
  AlertCircle,
  ArrowLeft,
  Pencil,
  Trash2,
  UserCog,
  UserPlus,
  MailPlus,
  Copy,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_META: Record<string, { label: string; dot: string }> = {
  CONFIRMED: { label: 'Confirmed', dot: 'bg-green-500' },
  FORCE_CHANGE_PASSWORD: { label: 'Must change password', dot: 'bg-amber-500' },
  RESET_REQUIRED: { label: 'Reset required', dot: 'bg-amber-500' },
  UNCONFIRMED: { label: 'Unconfirmed', dot: 'bg-muted-foreground/50' },
  ARCHIVED: { label: 'Archived', dot: 'bg-red-500' },
  COMPROMISED: { label: 'Compromised', dot: 'bg-red-500' },
  UNKNOWN: { label: 'Unknown', dot: 'bg-muted-foreground/50' },
};

function StatusDot({ status }: { status: string | null }) {
  const meta = STATUS_META[status ?? ''] ?? { label: status ?? 'None', dot: 'bg-muted-foreground/30' };
  return (
    <Tooltip label={meta.label} side="bottom">
      <span className={cn('h-2 w-2 rounded-full shrink-0', meta.dot)} />
    </Tooltip>
  );
}

function Avatar({ name, large }: { name: string; large?: boolean }) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground font-semibold',
        large ? 'h-12 w-12 text-sm' : 'h-8 w-8 text-[11px]'
      )}
    >
      {initials || '?'}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

export function UsersPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();
  const { data: users, isLoading: usersLoading, error: usersError, refetch } = useUsers();
  // Fetched for every signed-in user; the upstream resolver rejects
  // non-SuperAdmins, in which case badges simply don't render.
  const { data: cognitoUsers } = useCognitoGroups(true);
  const { data: user, isLoading: userLoading } = useUser(id || '');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState<'all' | 'super' | 'admin' | 'regular'>('all');
  const [sort, setSort] = useState<'name' | 'joined' | 'active'>('name');
  const activeFilterCount = [statusFilter, typeFilter, roleFilter].filter((f) => f !== 'all').length;

  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addToTenantsOpen, setAddToTenantsOpen] = useState(false);
  const [roleEdit, setRoleEdit] = useState<{ id: string; tenantName: string; tenantRole: string | null } | null>(null);
  const [removeMembership, setRemoveMembership] = useState<{ id: string; tenantName: string } | null>(null);
  const [batchMemberships, setBatchMemberships] = useState<{ id: string; tenantName: string }[] | null>(null);
  const [removeUserOpen, setRemoveUserOpen] = useState(false);

  const membershipSel = useSelection();
  const membershipOps = useUserMembershipOps(id ?? '');

  const adminInfo = useMemo(() => {
    // Keyed by BOTH Cognito username (UUID) and lowercase email — profile ids
    // are supposed to equal the username, but email is the drift-proof join.
    const superAdmins = new Set<string>();
    const admins = new Set<string>();
    const typeAdmins = new Map<string, string[]>();
    const index = (key: string | null | undefined, cu: { groups: string[]; typeOfTenants: string[] }) => {
      if (!key) return;
      if (cu.groups.includes('SuperAdmin')) superAdmins.add(key);
      if (cu.groups.includes('Admin')) admins.add(key);
      if (cu.typeOfTenants.length > 0) typeAdmins.set(key, cu.typeOfTenants);
    };
    for (const cu of cognitoUsers ?? []) {
      index(cu.username, cu);
      index(cu.email?.toLowerCase(), cu);
    }
    return { superAdmins, admins, typeAdmins };
  }, [cognitoUsers]);

  const hasSuperAdmin = (u: { id: string; email: string }) =>
    adminInfo.superAdmins.has(u.id) || adminInfo.superAdmins.has(u.email.toLowerCase());
  const hasCognitoAdmin = (u: { id: string; email: string }) =>
    adminInfo.admins.has(u.id) || adminInfo.admins.has(u.email.toLowerCase());
  const typeAdminScope = (u: { id: string; email: string }) =>
    adminInfo.typeAdmins.get(u.id) ?? adminInfo.typeAdmins.get(u.email.toLowerCase());

  // Admin = Cognito 'Admin' group, or type-level admin claim (custom:type_of_tenant),
  // or ADMIN role in at least one membership
  const isUserAdmin = (u: { id: string; email: string; tenants: { items: Array<{ tenantRole: string | null }> } }) =>
    hasCognitoAdmin(u) || !!typeAdminScope(u) || u.tenants.items.some((m) => m.tenantRole === 'ADMIN');

  const statuses = useMemo(
    () => [...new Set((users ?? []).map((u) => u.cognitoUserStatus).filter(Boolean))] as string[],
    [users]
  );
  const tenantTypes = useMemo(
    () =>
      [
        ...new Set(
          (users ?? []).flatMap((u) => u.tenants.items.map((m) => m.typeOfTenantID).filter(Boolean))
        ),
      ].sort() as string[],
    [users]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (users ?? [])
      .filter((u) => {
        if (q) {
          const name = userName(u).toLowerCase();
          if (!name.includes(q) && !u.email.toLowerCase().includes(q)) return false;
        }
        if (statusFilter !== 'all' && u.cognitoUserStatus !== statusFilter) return false;
        if (typeFilter !== 'all' && !u.tenants.items.some((m) => m.typeOfTenantID === typeFilter)) return false;
        if (roleFilter === 'super' && !hasSuperAdmin(u)) return false;
        if (roleFilter === 'admin' && !(isUserAdmin(u) && !hasSuperAdmin(u))) return false;
        if (roleFilter === 'regular' && (isUserAdmin(u) || hasSuperAdmin(u))) return false;
        return true;
      });
    list.sort((a, b) => {
      if (sort === 'name') return userName(a).localeCompare(userName(b));
      if (sort === 'joined') return (b.joinedAt ?? '').localeCompare(a.joinedAt ?? '');
      return (b.lastAuthenticatedAt ?? '').localeCompare(a.lastAuthenticatedAt ?? '');
    });
    return list;
  }, [users, search, statusFilter, typeFilter, roleFilter, sort, cognitoUsers]);

  const memberships = useMemo(
    () =>
      (user?.tenants.items ?? [])
        .slice()
        .sort((a, b) => (a.tenant?.name ?? '').localeCompare(b.tenant?.name ?? '')),
    [user]
  );
  const membershipIds = useMemo(() => memberships.map((m) => m.id), [memberships]);

  // Arrow-key browsing through the filtered user list.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return;
      if (filtered.length === 0) return;
      e.preventDefault();
      const idx = filtered.findIndex((u) => u.id === id);
      const nextIdx =
        idx === -1 ? 0 : e.key === 'ArrowDown' ? Math.min(idx + 1, filtered.length - 1) : Math.max(idx - 1, 0);
      const next = filtered[nextIdx];
      if (next && next.id !== id) navigate(`/users/${next.id}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtered, id, navigate]);

  useEffect(() => {
    if (id) document.getElementById(`user-row-${id}`)?.scrollIntoView({ block: 'nearest' });
  }, [id]);

  const confirmRemoveMembership = async () => {
    if (!removeMembership) return;
    try {
      await membershipOps.remove.mutateAsync(removeMembership.id);
      toast.success(`Removed from ${removeMembership.tenantName}`);
      setRemoveMembership(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Remove failed');
    }
  };

  const confirmBatchMemberships = async () => {
    if (!batchMemberships) return;
    let failed = 0;
    for (const m of batchMemberships) {
      try {
        await membershipOps.remove.mutateAsync(m.id);
      } catch {
        failed++;
      }
    }
    const n = batchMemberships.length;
    setBatchMemberships(null);
    membershipSel.exit();
    if (failed > 0) toast.error(`${failed} of ${n} memberships could not be removed`);
    else toast.success(`Removed from ${n} tenant${n === 1 ? '' : 's'}`);
  };

  return (
    <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-6 lg:h-[calc(100dvh-4rem)]">
      {/* ---- List pane ---- */}
      <div className={cn('min-h-0 flex-col', id ? 'hidden lg:flex' : 'flex')}>
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center justify-between">
              <h1 className="text-sm font-semibold">Users</h1>
              <div className="flex items-center gap-1.5">
                <Badge variant="secondary">{filtered.length}</Badge>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setInviteOpen(true)}>
                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                  Invite
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or email…"
                  className="pl-8 h-8 text-xs"
                />
              </div>
              <FilterPopover
                activeCount={activeFilterCount}
                onReset={() => {
                  setStatusFilter('all');
                  setTypeFilter('all');
                  setRoleFilter('all');
                }}
              >
                <Field label="Status">
                  <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
                    <option value="all">Any status</option>
                    {statuses.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_META[s]?.label ?? s}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Tenant type">
                  <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
                    <option value="all">Any type</option>
                    {tenantTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Role">
                  <Select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
                    className="[&>select]:h-8 [&>select]:text-xs"
                  >
                    <option value="all">Any role</option>
                    <option value="super">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="regular">Regular user</option>
                  </Select>
                </Field>
                <Field label="Sort by">
                  <Select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="[&>select]:h-8 [&>select]:text-xs">
                    <option value="name">Name A–Z</option>
                    <option value="joined">Recently joined</option>
                    <option value="active">Last active</option>
                  </Select>
                </Field>
              </FilterPopover>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-1.5 space-y-0.5">
            {usersLoading ? (
              <div className="space-y-1.5 p-1">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="h-11 w-full" />
                ))}
              </div>
            ) : usersError ? (
              <EmptyState
                icon={AlertCircle}
                title="Failed to load users"
                description={usersError.message}
                className="m-2 py-8"
                action={
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : filtered.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-muted-foreground">No users match the current filters</p>
            ) : (
              filtered.map((u) => {
                const name = userName(u);
                return (
                  <Link
                    key={u.id}
                    id={`user-row-${u.id}`}
                    to={`/users/${u.id}`}
                    className={cn(
                      'flex items-center gap-2.5 rounded-md px-2.5 py-2 transition-colors',
                      id === u.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50'
                    )}
                  >
                    <Avatar name={name} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-medium truncate">{name}</span>
                      <span className="block text-[11px] text-muted-foreground truncate">{u.email}</span>
                    </span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {hasSuperAdmin(u) && (
                        <Tooltip label="Super Admin — full access" side="bottom">
                          <Shield className="h-3 w-3 text-destructive shrink-0" />
                        </Tooltip>
                      )}
                      {isUserAdmin(u) && (
                        <Badge variant="outline" className="text-[9px]">
                          Admin
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-[9px]">
                        {u.tenants.items.length} <Building2 className="h-2.5 w-2.5 ml-0.5" />
                      </Badge>
                      <StatusDot status={u.cognitoUserStatus} />
                    </span>
                  </Link>
                );
              })
            )}
          </div>
        </Card>
      </div>

      {/* ---- Detail pane ---- */}
      <div className={cn('min-h-0 lg:overflow-auto', !id && 'hidden lg:block')}>
        {!id ? (
          <div className="h-full flex items-center justify-center">
            <EmptyState
              icon={Users}
              title="Select a user"
              description="Pick from the list on the left, browse with ↑ / ↓, or press ⌘K to jump."
              className="w-full"
            />
          </div>
        ) : userLoading ? (
          <div className="space-y-4">
            <Card className="p-5 space-y-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </Card>
            <Card className="p-5 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </Card>
          </div>
        ) : !user ? (
          <EmptyState
            icon={AlertCircle}
            title="User not found"
            description="This profile doesn't exist (anymore)."
            action={
              <Button variant="outline" size="sm" onClick={() => navigate('/users')}>
                Back to users
              </Button>
            }
          />
        ) : (
          <div className="space-y-4 animate-enter">
            {/* Header */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8 shrink-0" onClick={() => navigate('/users')} aria-label="Back to users">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Avatar name={userName(user)} large />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold tracking-tight truncate">{userName(user)}</h2>
                  <StatusDot status={user.cognitoUserStatus} />
                  {hasSuperAdmin(user) && (
                    <Badge variant="destructive" className="text-[10px] shrink-0">
                      <Shield className="h-2.5 w-2.5 mr-1" />
                      Super Admin
                    </Badge>
                  )}
                  {isUserAdmin(user) && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      Admin
                      {typeAdminScope(user)?.length ? ` · ${typeAdminScope(user)!.join(', ')}` : ''}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{user.email}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(user.email);
                      toast.success('Email copied');
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    aria-label="Copy email"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
                {user.roleDescription && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{user.roleDescription}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit
                </Button>
                <UserActionsMenu user={user} isSuperAdmin={isSuperAdmin} onRemove={() => setRemoveUserOpen(true)} />
              </div>
            </div>

            {/* Profile facts */}
            <Card className="p-5">
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 text-xs">
                <div>
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="font-medium mt-0.5 flex items-center gap-1.5">
                    <StatusDot status={user.cognitoUserStatus} />
                    {STATUS_META[user.cognitoUserStatus ?? '']?.label ?? user.cognitoUserStatus ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Joined</dt>
                  <dd className="font-medium mt-0.5" title={user.joinedAt ?? undefined}>{fmtDate(user.joinedAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Last invited</dt>
                  <dd className="font-medium mt-0.5" title={user.lastInvitedAt ?? undefined}>{fmtDate(user.lastInvitedAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Last active</dt>
                  <dd className="font-medium mt-0.5" title={user.lastAuthenticatedAt ?? undefined}>{fmtDate(user.lastAuthenticatedAt)}</dd>
                </div>
              </dl>
            </Card>

            {/* Memberships */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Tenants</h3>
                <Badge variant="secondary">{memberships.length}</Badge>
                <span className="ml-auto flex items-center gap-1">
                  {memberships.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => (membershipSel.active ? membershipSel.exit() : membershipSel.start())}
                    >
                      {membershipSel.active ? 'Cancel' : 'Select'}
                    </Button>
                  )}
                  {!membershipSel.active && (
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAddToTenantsOpen(true)}>
                      <MailPlus className="h-3.5 w-3.5 mr-1" />
                      Add to tenants
                    </Button>
                  )}
                </span>
              </div>

              <div className="divide-y divide-border/60 max-h-[50vh] lg:max-h-none overflow-auto">
                {memberships.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-6 text-center">
                    Not a member of any tenant yet — use “Add to tenants”.
                  </p>
                ) : (
                  memberships.map((m, mi) => {
                    const tenantName = m.tenant?.name ?? m.tenantID;
                    return (
                      <div
                        key={m.id}
                        onClick={
                          membershipSel.active
                            ? (e) => membershipSel.toggle(m.id, mi, membershipIds, e.shiftKey)
                            : undefined
                        }
                        className={cn(
                          'group flex items-center gap-2.5 py-2.5 transition-colors',
                          membershipSel.active ? 'cursor-pointer select-none px-1' : 'hover:bg-muted/40 px-1',
                          membershipSel.active && membershipSel.selected.has(m.id) && 'bg-accent/60'
                        )}
                      >
                        {membershipSel.active && (
                          <input
                            type="checkbox"
                            readOnly
                            checked={membershipSel.selected.has(m.id)}
                            className="h-3.5 w-3.5 shrink-0 accent-primary pointer-events-none"
                          />
                        )}
                        <Link
                          to={`/tenants/${m.tenantID}`}
                          onClick={(e) => membershipSel.active && e.preventDefault()}
                          className={cn(
                            'flex-1 min-w-0 text-xs font-medium truncate hover:text-primary transition-colors',
                            membershipSel.active && 'pointer-events-none'
                          )}
                          title={tenantName}
                        >
                          {tenantName}
                        </Link>
                        {m.typeOfTenantID && (
                          <Badge variant="secondary" className="shrink-0 text-[9px]">
                            {m.typeOfTenantID}
                          </Badge>
                        )}
                        {m.tenantRole && m.tenantRole !== 'NONE' && (
                          <Badge variant="outline" className="shrink-0 text-[9px]">
                            {m.tenantRole}
                          </Badge>
                        )}
                        {!membershipSel.active && (
                          <span className="flex items-center gap-0.5 shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                            <Tooltip label="Change role">
                              <button
                                type="button"
                                onClick={() =>
                                  setRoleEdit({ id: m.id, tenantName, tenantRole: m.tenantRole })
                                }
                                className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                                aria-label={`Edit role in ${tenantName}`}
                              >
                                <UserCog className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                            <Tooltip label="Remove from tenant">
                              <button
                                type="button"
                                onClick={() => setRemoveMembership({ id: m.id, tenantName })}
                                className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                aria-label={`Remove from ${tenantName}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {membershipSel.active && (
                <SelectionBar
                  count={membershipSel.selected.size}
                  actionLabel="Remove"
                  pending={membershipOps.remove.isPending}
                  onAction={() =>
                    setBatchMemberships(
                      memberships
                        .filter((m) => membershipSel.selected.has(m.id))
                        .map((m) => ({ id: m.id, tenantName: m.tenant?.name ?? m.tenantID }))
                    )
                  }
                  onDone={membershipSel.exit}
                />
              )}
            </Card>

            <EditUserDialog key={user.id} user={user} open={editOpen} onClose={() => setEditOpen(false)} />
            <AddToTenantsDialog user={user} open={addToTenantsOpen} onClose={() => setAddToTenantsOpen(false)} />
            <EditUserRoleDialog
              key={roleEdit?.id ?? 'none'}
              userId={user.id}
              membership={roleEdit}
              open={roleEdit !== null}
              onClose={() => setRoleEdit(null)}
            />
            <ConfirmDialog
              open={removeMembership !== null}
              title={`Remove from ${removeMembership?.tenantName ?? ''}?`}
              description="The user loses access to this tenant and its dashboards."
              confirmLabel="Remove"
              destructive
              loading={membershipOps.remove.isPending}
              onConfirm={confirmRemoveMembership}
              onCancel={() => setRemoveMembership(null)}
            />
            <ConfirmDialog
              open={batchMemberships !== null}
              title={`Remove from ${batchMemberships?.length ?? 0} tenants?`}
              description="The user loses access to these tenants and their dashboards."
              confirmLabel="Remove all"
              destructive
              loading={membershipOps.remove.isPending}
              onConfirm={confirmBatchMemberships}
              onCancel={() => setBatchMemberships(null)}
            />
            <RemoveUserDialog
              user={user}
              open={removeUserOpen}
              onClose={() => setRemoveUserOpen(false)}
              onRemoved={() => {
                setRemoveUserOpen(false);
                navigate('/users');
              }}
            />
          </div>
        )}
      </div>

      <InviteUserDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}
